import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, signal, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  debounceTime,
  finalize,
  forkJoin,
  Observable,
  Subject,
  Subscription,
  switchMap,
  take,
  tap
} from 'rxjs';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { ApiErrorCodes } from '../../../../core/enums/ErrorCodes';
import { BroadcastChannelMessageTypes } from '../../../../core/enums/BroadcastChannelMessageTypes';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { BroadcastChannelService } from '../../../../core/services/BroadcastChannelService';
import { LoggingService } from '../../../../core/services/LoggingService';
import { AlertModalComponent } from '../../../../shared/components/alert-modal/alert-modal.component';
import { ChipComponent } from '../../../../shared/components/chip/chip.component';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { ContentBoxComponent } from '../../../../shared/components/content-box/content-box.component';
import {
  ContentEditableInputComponent
} from '../../../../shared/components/content-editable-input/content-editable-input.component';
import { DescriptionModalComponent } from '../../../../shared/components/description-modal/description-modal.component';
import { EcTerminalActionLogsModalComponent } from '../../../../shared/components/ec-terminal-action-logs-modal/ec-terminal-action-logs-modal.component';
import { KeyboardComponent } from '../../../../shared/components/keyboard/keyboard.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { PdfViewerModalComponent } from '../../../../shared/components/pdf-viewer-modal/pdf-viewer-modal.component';
import {
  ScrollableWrapperComponent
} from '../../../../shared/components/scrollable-wrapper/scrollable-wrapper.component';
import { StepperComponent } from '../../../../shared/components/stepper/stepper.component';
import { heightDirective } from '../../../../shared/directives/height.directive';
import { WebCashPermissionControlDirective } from '../../../auth/core/directives/web-cash-permission-control.directive';
import { TooltipDirective } from '../../../../shared/directives/tooltip.directive';
import { TOOLTIP_DELAY } from '../../../../shared/constants/tooltip';
import { WidthDirective } from '../../../../shared/directives/width.directive';
import { ArrayByLengthPipe } from '../../../../shared/pipes/array-by-length.pipe';
import { FromCurrencyPipe } from '../../../../shared/pipes/from-currency.pipe';
import { LocaleDatetimeFormatPipe } from '../../../../shared/pipes/locale-datetime-format.pipe';
import { NumberToCurrencyPipe } from '../../../../shared/pipes/number-to-currency.pipe';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import {
  CardCodeInputModalComponent
} from '../../../cards/modals/card-code-input-modal/card-code-input-modal.component';
import { ICustomer } from '../../../customers/core/interfaces/ICustomer';
import {
  CustomersSearchModalComponent
} from '../../../customers/modals/customers-search-modal/customers-search-modal.component';
import { ReservationsService } from '../../../reservations/core/services/ReservationsService';
import { ReservationsStore } from '../../../reservations/core/stores/ReservationsStore';
import { ShowsStore } from '../../../shows/core/stores/ShowsStore';
import { StatisticsService } from '../../../statistics/core/services/StatisticsService';
import { TerminalParameterKeys } from '../../../terminals/core/enums/TerminalParameterKeys';
import { TerminalPrintReceiptParamValues } from '../../../terminals/core/enums/TerminalPrintReceiptParamValues';
import { TransactionsService } from '../../../transactions/core/services/TransactionsService';
import { EcReceiptPrintService } from '../../../worker/core/services/EcReceiptPrintService';
import { WorkerService } from '../../../worker/core/services/WorkerService';
import { PaymentButtonComponent } from '../../components/payment-button/payment-button.component';
import { CartItemTypes } from '../../core/enums/CartItemTypes';
import { CartPaymentMethods } from '../../core/enums/CartPaymentMethods';
import { CartPaymentStatuses } from '../../core/enums/CartPaymentStatuses';
import { CartStatuses } from '../../core/enums/CartStatuses';
import { ICartCheckout } from '../../core/interfaces/ICartCheckout';
import { ICartItem } from '../../core/interfaces/ICartItem';
import { ICartPayment } from '../../core/interfaces/ICartPayment';
import { ISelectedPosition } from '../../core/interfaces/ISelectedPosition';
import { IsVoucherStackablePipe } from '../../core/pipes/is-voucher-stackable.pipe';
import { CartItemDisplayTotalPipe } from '../../core/pipes/cart-item-display-total.pipe';
import { CartService } from '../../core/services/CartService';
import { NextShowAfterSaleUtils } from '../../core/utils/NextShowAfterSaleUtils';
import { PaySeparatelyService } from '../../core/services/PaySeparatelyService';
import { CartStore } from '../../core/stores/CartStore';
import { PaySeparatelyStore } from '../../core/stores/PaySeparatelyStore';
import { CinevilleUtils } from '../../core/utils/CinevilleUtils';
import { resolveEcTerminalAmount } from '../../core/utils/resolve-ec-terminal-amount';
import {
  PaymentMethodPickerModalComponent
} from '../payment-method-picker-modal/payment-method-picker-modal.component';
import {
  PriceCategoryPickerModalComponent
} from '../price-category-picker-modal/price-category-picker-modal.component';
import {
  ReservationCustomerNameModalComponent
} from '../reservation-customer-name-modal/reservation-customer-name-modal.component';
import { ReportKeys } from '../../../reports/core/enums/ReportKeys';
import { ReportsService } from '../../../reports/core/services/ReportsService';

@Component({
  selector: 'app-summary-modal',
  standalone: true,
  imports: [
    ModalComponent,
    ContentBoxComponent,
    ScrollableWrapperComponent,
    FormsModule,
    KeyboardComponent,
    ContentEditableInputComponent,
    NgTemplateOutlet,
    NumberToCurrencyPipe,
    heightDirective,
    WidthDirective,
    FromCurrencyPipe,
    TranslatePipe,
    PaymentButtonComponent,
    ReservationCustomerNameModalComponent,
    PaymentMethodPickerModalComponent,
    ChipComponent,
    PriceCategoryPickerModalComponent,
    CardCodeInputModalComponent,
    DescriptionModalComponent,
    CustomersSearchModalComponent,
    PdfViewerModalComponent,
    LocaleDatetimeFormatPipe,
    EcTerminalActionLogsModalComponent,
    StepperComponent,
    ArrayByLengthPipe,
    IsVoucherStackablePipe,
    CartItemDisplayTotalPipe,
    WebCashPermissionControlDirective,
    TooltipDirective
  ],
  templateUrl: './summary-modal.component.html',
  styleUrl: './summary-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SummaryModalComponent extends BaseModalComponent {

  protected readonly tooltipDelay = TOOLTIP_DELAY;

  @ViewChild(ContentEditableInputComponent) contentEditableInputComponent: ContentEditableInputComponent;
  @ViewChild('reservationCustomerNameModalComponent') reservationCustomerNameModalComponent: ReservationCustomerNameModalComponent;
  @ViewChild('paymentMethodPickerModalComponent') paymentMethodPickerModalComponent: PaymentMethodPickerModalComponent;
  @ViewChild('descriptionModalComponent') descriptionModalComponent: DescriptionModalComponent;
  @ViewChild('cardCodeInputModalComponent') cardCodeInputModalComponent: CardCodeInputModalComponent;
  @ViewChild('customersSearchModalComponent') customersSearchModalComponent: CustomersSearchModalComponent;
  @ViewChild('pdfViewerModalComponent') pdfViewerModalComponent: PdfViewerModalComponent;
  @ViewChild('ecTerminalActionLogsModalComponent') ecTerminalActionLogsModalComponent: EcTerminalActionLogsModalComponent;

  public selectedCustomerForInvoice: ICustomer;
  public refundCartPaymentMethod = CartPaymentMethods.Cash;
  public refundCardCode: string;
  public description: string;
  public transactionRemark: string;
  public customerName: string;
  public reservationCode: string;
  public amountValue: string;
  public readonly updateCartItem$ = new Subject<ICartItem>();
  public readonly isCheckoutProcessing = signal(false);
  public readonly isCartFinishing = signal(false);
  public readonly isPaymentAdding = signal(false);
  public readonly isPrintLastReceiptLoading = signal(false);
  public readonly isEcChargeConfirmed = signal(false);
  public readonly cartPaymentMethods = CartPaymentMethods;
  public readonly cartStatuses = CartStatuses;
  public readonly cartPaymentStatuses = CartPaymentStatuses;
  public readonly cartItemTypes = CartItemTypes;
  public readonly cartStore = inject(CartStore);
  public readonly reservationsStore = inject(ReservationsStore);
  public readonly showsStore = inject(ShowsStore);
  public readonly paySeparatelyStore = inject(PaySeparatelyStore);
  public readonly terminalPrintReceiptParamValues = TerminalPrintReceiptParamValues;

  private _currentModalType: 'cancel' | 'freeEntry' | null = null;
  private _autoCloseCart = false;
  private _quickCloseAfterInvoice = false;
  private _isOrderAmountSet = true;
  private _cartCheckout: ICartCheckout;
  private _updateCartItemSub: Subscription;
  private readonly _cartService = inject(CartService);
  private readonly _reservationsService = inject(ReservationsService);
  private readonly _transactionsService = inject(TransactionsService);
  private readonly _statisticsService = inject(StatisticsService);
  private readonly _workerService = inject(WorkerService);
  private readonly _ecReceiptPrintService = inject(EcReceiptPrintService);
  private readonly _broadcastChannelService = inject(BroadcastChannelService);
  private readonly _paySeparatelyService = inject(PaySeparatelyService);
  private readonly _loggingService = inject(LoggingService);
  private readonly _router = inject(Router);
  private readonly _reportsService = inject(ReportsService);

  public get isPartialPaymentPossible(): boolean {
    return this._paySeparatelyService.isPartialPaymentPossible;
  }

  private buildCartSnapshot(): Record<string, unknown> {
    try {
      const cart = this.cartStore.cart();

      if (!cart) {
        return { cart: null };
      }

      return {
        cartId: cart.id,
        cartStatus: cart.status,
        transactionId: cart.transactionId,
        totalPrice: cart.totalPrice,
        paidAmount: cart.paidAmount,
        remainingAmount: cart.remainingAmount,
        isRefundTransaction: cart.isRefundTransaction,
        items: cart.items?.map(item => ({
          itemType: item.itemType,
          itemId: item.itemId,
          price: item.price,
          quantity: item.quantity,
          cardId: item.cardId
        })),
        payments: cart.payments?.map(payment => ({
          method: payment.method,
          amount: payment.amount,
          status: payment.status
        }))
      };
    } catch {
      return { snapshotError: true };
    }
  }

  public constructor() {
    super();

    effect(() => {
      if (
        this.cartStore.cart() &&
        this.cartStore.cart().items.length === 0 &&
        this.modalComponent &&
        this.modalComponent.isOpened()
      ) {
        this.close();
      }

      if (this.cartStore.cart()) {
        this.handleKeyboardClear();
      }
    }, { allowSignalWrites: true });

    effect(() => {
      if (!this.paySeparatelyStore.isPaySeparatelyModeEnabled()) return;
      const maxAmount = this.paySeparatelyStore.selectedItemsTotal();
      if (maxAmount <= 0) return;
      const currentCents = Number(this.amountValue || '0') || 0;
      const currentAmount = currentCents / 100;
      if (currentAmount <= maxAmount) return;
      this.updateAmountValue((maxAmount * 100).toString());
    }, { allowSignalWrites: true });
  }

  public override open(val?: null): void {
    this._cartCheckout = null;
    this.selectedCustomerForInvoice = null;
    this.refundCartPaymentMethod = CartPaymentMethods.Cash;
    this.refundCardCode = null;
    this.description = null;
    this.transactionRemark = null;
    this.customerName = null;
    this.reservationCode = null;
    this.amountValue = null;
    this._currentModalType = null;
    this.isEcChargeConfirmed.set(false);
    this._quickCloseAfterInvoice = false;

    this._broadcastChannelService.cashChannelPostMessage({
      type: BroadcastChannelMessageTypes.DISABLE_CUSTOMER_SCREEN,
      data: {}
    });

    super.open(val);
    this.setOrderAmount();
    this.cdr.detectChanges();

    this._updateCartItemSub = this.updateCartItem$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(500),
        switchMap(item => this._cartService.updateCartItem([item]))
      )
      .subscribe({
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`update-cart-item/notifications/${error.message}.Message`)
          );
        }
      });

    if (this.paySeparatelyStore.isPaySeparatelyModeEnabled()) {
      this.paySeparatelyStore.loadFromStorage();
      const selectedTotal = this.paySeparatelyStore.selectedItemsTotal();
      if (selectedTotal > 0) {
        this.updateAmountValue((selectedTotal * 100).toString());
      }
    }
  }

  public override close(): void {
    if (this.isEcChargeConfirmed() && this.cartStore.cart()?.status === CartStatuses.Pending) {
      return;
    }

    this._updateCartItemSub?.unsubscribe();

    this._broadcastChannelService.cashChannelPostMessage({
      type: BroadcastChannelMessageTypes.ENABLE_CUSTOMER_SCREEN,
      data: {}
    });

    super.close();
  }

  public clearCart(btn: HTMLButtonElement): void {
    btn.classList.add('disabled');

    this._cartService.clearCart()
      .pipe(
        tap(() => {
          this.resetCheckoutProcessingState();
          this._paySeparatelyService.disablePaySeparatelyMode();
        }),
        finalize(() => {
          btn.classList.remove('disabled');
          this.reservationsStore.rebookReservation.set(null);
        })
      )
      .subscribe({
        error: ({ error }: ApiErrorResponse) => {
          this.resetCheckoutProcessingState();
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`clear-cart/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public resetCheckoutProcessingState(): void {
    this.isCheckoutProcessing.set(false);
    this.isPaymentAdding.set(false);
    this.isCartFinishing.set(false);
  }

  public openSafe(): void {
    if (!this.permissionService.hasPermission(this.permissionKeys.WebCxCashSafes)) {
      return;
    }

    this._workerService.openSafe().subscribe({
      error: ({ error }: ApiErrorResponse) => {
        this.notificationService.pushError(
          'Fehler'.trans('notifications/error.Title'),
          error.message.trans(`open-safe/notifications/${error.message}.Message`)
        );
      }
    });
  }

  public choosePayment(type: CartPaymentMethods, options?: { autoClose?: boolean }): void {
    if (!this.terminalsStore.isPaymentMethodAllowed(type)) {
      return;
    }

    const autoClose = !!options?.autoClose;
    const amountToCompare = this.paySeparatelyStore.isPaySeparatelyModeEnabled() ? this.paySeparatelyStore.selectedItemsTotal() : this.cartStore.cart().remainingAmount;
    switch (type) {
      case CartPaymentMethods.Cash: {
        if (this.contentEditableInputComponent.value.fromCurrency()) {

          if (this.contentEditableInputComponent.value.fromCurrency() === amountToCompare) {
            this.addPayment(
              CartPaymentMethods.Cash,
              amountToCompare,
              {},
              () => this.onCheckoutButtonClick()
            );
          } else {
            this.addPayment(type);
          }
        }
      }
        break;

      case CartPaymentMethods.Card: {
        const keypadAmount = this.contentEditableInputComponent
          ? this.contentEditableInputComponent.value.fromCurrency()
          : amountToCompare;
        const amount = resolveEcTerminalAmount(amountToCompare, keypadAmount);

        if (this.isEcStandalone) {
          this.openStandaloneEcConfirmationModal(
            amount,
            () => {
              if (autoClose) {
                this.addPayment(
                  CartPaymentMethods.Card,
                  amount,
                  {},
                  () => this.onCheckoutButtonClick(true)
                );
                return;
              }

              this.addPayment(CartPaymentMethods.Card, amount);
            }
          );
          break;
        }

        if (this.isEcChargeConfirmed()) {
          this.recordConfirmedCardPaymentAndCheckout(amount, autoClose);
          break;
        }

        this.ecTerminalActionLogsModalComponent.open(amount);

        this._cartService.startCardPayment(amount)
          .subscribe({
            next: () => {
              this.isEcChargeConfirmed.set(true);
              // Legacy CXCash parity (CDlgCXCash::PrintEC): right after the card is charged
              // and before the cinema transaction/ticket print, prompt for the customer copy
              // and forward the buffered EC slip to the POS/Epson printer. Merchant copy always prints.
              this.printBufferedEcReceiptWithPrompt(() => {
                this.recordConfirmedCardPaymentAndCheckout(amount, autoClose);
              });
            },
            error: ({ error }: ApiErrorResponse) => {
              // Legacy CXCash parity (PrintEC(-1)): on cancel/decline still flush the buffered
              // EC slip (merchant decline copy). Best-effort — a missing buffer is ignored.
              if (this.isEcPrintLocationPos && !this.isEcSumUp) {
                this._ecReceiptPrintService.print(false, { silent: true });
              }
              this.ecTerminalActionLogsModalComponent.close();
              this.notificationService.pushError(
                'Fehler'.trans('notifications/error.Title'),
                this.cardErrorText(error.message, 'start-card-payment')
              );
            }
          });
      }
        break;

      case CartPaymentMethods.Invoice: {
        if (this.selectedCustomerForInvoice) {
          const obj = {};
          const closeAfter = this._quickCloseAfterInvoice;
          this._quickCloseAfterInvoice = false;

          if (!this.selectedCustomerForInvoice.isAnonym) {
            obj['customerId'] = this.selectedCustomerForInvoice.id;
          }

          if (closeAfter) {
            this.addPayment(
              type,
              amountToCompare,
              obj,
              () => this.onCheckoutButtonClick(true)
            );
          } else {
            this.addPayment(type, amountToCompare, obj);
          }
        } else {
          this.customersSearchModalComponent.open();
        }
      }
        break;
    }
  }

  public get showVoucherOutOption(): boolean {
    const cart = this.cartStore.cart();

    if (!cart) return false;

    return this.cartStore.cartItemsCashedVouchers().length > 0;
  }

  public get isCartInFinishedState(): boolean {
    return [CartStatuses.CheckedOut, CartStatuses.PartiallyPaid]
      .includes(this.cartStore.cart()?.status);
  }

  public get isInvoicePayment(): boolean {
    return [CartPaymentMethods.Invoice, CartPaymentMethods.InvoiceOut]
      .includes(this._cartCheckout?.item.basket.payments[0]?.method ?? this.cartStore.cart().payments[0]?.method);
  }

  public get areOnlyReservations(): boolean {
    return this.cartStore.cart().items.every(i => i.isReservation) &&
      this.cartStore.cart().payments.length === 0;
  }

  public get isCheckoutOrPartiallyPaid(): boolean {
    const status = this.cartStore.cart()?.status;
    return status === CartStatuses.CheckedOut || status === CartStatuses.PartiallyPaid;
  }

  public continue(): void {
    this.isCartFinishing.set(true);

    this._cartService.getCart()
      .pipe(
        finalize(() => this.isCartFinishing.set(false)),
        tap(() => {
          this._broadcastChannelService.cashChannelPostMessage({
            type: BroadcastChannelMessageTypes.CART_CHANGES,
            data: {
              cart: this.cartStore.cart(),
              cartAction: {
                actionType: 'reloadAfterPartialPayment'
              }
            }
          });

          this._cartService.triggerCartAction(this.cartStore.cart(), 'reloadAfterPartialPayment');
        })
      )
      .subscribe({
        next: () => {
          this.resetModalState();

          if (!this.isPartialPaymentPossible) {
            this._paySeparatelyService.disablePaySeparatelyMode();
            const remainingAmount = this.cartStore.cart().remainingAmount;
            queueMicrotask(() => this.updateAmountValue((remainingAmount * 100).toString()));
          } else {
            const selectedTotal = this.paySeparatelyStore.selectedItemsTotal();
            queueMicrotask(() => this.updateAmountValue((selectedTotal * 100).toString()));
          }
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`get-cart/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public closeCart(): void {
    const apiCalls: Observable<any>[] = [
      this._cartService.createCart(true),
    ];

    if (this.showsStore.selectedShow()) {
      apiCalls.push(
        this._reservationsService.getReservations(this.showsStore.selectedShow().id),
        this._transactionsService.getTransactions(this.showsStore.selectedShow().id)
      );
    }

    this.isCartFinishing.set(true);

    forkJoin(apiCalls)
      .pipe(
        finalize(() => this.isCartFinishing.set(false))
      )
      .subscribe({
        next: () => {
          this._paySeparatelyService.disablePaySeparatelyMode();
          this._statisticsService.getSessionStatistics().subscribe();
          this.navigateToNextUpcomingShowIfEnabled();
          this.close();
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`close-cart/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public detachCard(cardId?: number): void {
    if (this.isPreviewMode && !cardId) {
      return;
    }

    this._cartService.detachCardFromCart(cardId)
      .subscribe({
        next: () => {
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`cart/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public removeItemFromCart(itemId: number, position: number, btn: HTMLButtonElement): void {
    btn.classList.add('disabled');

    this._cartService
      .removePositionFromCart(itemId, position)
      .pipe(finalize(() => btn.classList.remove('disabled')))
      .subscribe({
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`remove-item-from-cart/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public removeOrDecreaseItemQuantity(itemId: number, position: number, btn: HTMLButtonElement): void {
    const cart = this.cartStore.cart();

    if (!cart) return;

    const item = cart.items.find(i => i.itemId === itemId && i.position === position);
    if (!item) return;

    if (this.paySeparatelyStore.isPaySeparatelyModeEnabled() && item.quantity > 1) {
      item.quantity -= 1;
      this.updateCartItem$.next(item);
    } else {
      this.removeItemFromCart(itemId, position, btn);
    }
  }

  public onCheckoutButtonClick(autoCloseCart = false): void {
    if (this.isCheckoutProcessing()) {
      return;
    }

    this._autoCloseCart = autoCloseCart;

    const hasCancelItems = this.areItemsToCancel;
    const hasFreeEntry = this.shouldShowFreeEntryRemarkModal();

    if (hasCancelItems) {
      this._currentModalType = 'cancel';
      this.description = null;
      this.descriptionModalComponent.open({ modalType: 'cancel' });

      return;
    }

    if (hasFreeEntry) {
      this._currentModalType = 'freeEntry';
      this.transactionRemark = null;
      this.descriptionModalComponent.open({ modalType: 'freeEntry' });

      return;
    }

    if (this.areReservations && !this.hasAnyCardWithCustomerId && !this.reservationsStore.rebookReservation()) {
      this.openReservationCustomerNameModal();

      return;
    }

    if (this.isRefund) {
      this.paymentMethodPickerModalComponent.open();

      return;
    }

    this.checkoutCart();
  }

  public get areReservations(): boolean {
    return this.cartStore.cart().items.filter(i => i.isReservation).length > 0;
  }

  public get hasAnyCardWithCustomerId(): boolean {
    return this.cartStore.customerCards().some(card => card.customerId != null);
  }

  public get areItemsToCancel(): boolean {
    return this.cartStore.cart().items.filter(i => i.itemType === CartItemTypes.CancelTicket || i.itemType === CartItemTypes.CancelVoucher).length > 0;
  }

  public removePayment(payment: ICartPayment, btn: HTMLButtonElement): void {
    btn.classList.add('disabled');

    this._cartService.removePaymentFromCart(payment.position)
      .pipe(
        finalize(() => btn.classList.remove('disabled'))
      )
      .subscribe({
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`remove-payment-from-cart/notifications/${error.message}.Message`)
          );
        }
      });
  }


  private selectedItemsHaveVouchersOrCardsOrTickets(selectedPositions: ISelectedPosition[] | null): boolean {
    const cart = this.cartStore.cart();
    if (!cart) return false;

    if (selectedPositions) {
      const printableTypes = [
        CartItemTypes.Ticket,
        CartItemTypes.Voucher,
        CartItemTypes.Card,
        CartItemTypes.LoadCard
      ];
      const itemsToCheck = cart.items.filter(item =>
        selectedPositions.some(p => p.itemId === item.itemId && p.position === item.position));
      return itemsToCheck.some(item => printableTypes.includes(item.itemType));
    }

    return this.cartStore.cartItemsVouchers().length > 0 ||
      this.cartStore.cartItemsTickets().length > 0 ||
      this.cartStore.cartItemsCards().length > 0;
  }

  public checkoutCart(): void {
    const checkoutCart = (): void => {
      this.isCheckoutProcessing.set(true);

      const selectedPositions = this.paySeparatelyStore.getSelectedPositions();
      const selectedItemsHavePrintableTypes = this.selectedItemsHaveVouchersOrCardsOrTickets(selectedPositions);

      this._cartService.checkoutCart(
        this.customerName,
        this.reservationCode,
        this.description,
        this.transactionRemark,
        selectedPositions,
        this.reservationsStore.rebookReservation()?.transactionId,
        this.showsStore.selectedShow()?.id
      )
      .pipe(tap(this.resetModalState.bind(this)))
        .subscribe({
          next: res => {
            this._cartCheckout = res.data;

            const isOnlineOrderCheckout = !!res.data.item.basket?.onlineOrderTransactionId &&
              res.data.item.basket.status !== CartStatuses.PartiallyPaid;

            if (isOnlineOrderCheckout) {
              this.showOnlineOrderDocuments(res.data.item.basket.transactionId ?? res.data.item.transactionId);
            }

            if (res.data.item.basket.status !== CartStatuses.PartiallyPaid) {
              this._paySeparatelyService.disablePaySeparatelyMode();
            } else {
              this.paySeparatelyStore.setAll();
            }

            const fn = (): void => {
              const transactionId = res.data.item.transactionId;
              const printKitchenSlip = (): void => {
                if (!transactionId) {
                  return;
                }

                this._workerService.printKitchen(transactionId).subscribe({
                  error: (err: ApiErrorResponse) => {
                    this._loggingService.captureApiError('print-kitchen', err);
                  }
                });
              };

              if (this.isInvoicePayment) {
                this.generateInvoice();
                printKitchenSlip();
                return;
              }

              const printReceiptParamValue = this.terminalParametersMap()
                [TerminalParameterKeys.CSH_PRINT_RECEIPT];

              if (
                printReceiptParamValue === TerminalPrintReceiptParamValues.PRINT_ALWAYS ||
                (
                  printReceiptParamValue === TerminalPrintReceiptParamValues.PRINT_KIOSK_PRODUCTS_ONLY &&
                  this.cartStore.cartItemsProductsAndPackages().length > 0
                )
              ) {
                this._workerService.printReceipt(transactionId)
                  .pipe(
                    finalize(() => {
                      this.isCheckoutProcessing.set(false);
                      printKitchenSlip();
                    })
                  )
                  .subscribe({
                    error: (err: ApiErrorResponse) => {
                      this._loggingService.captureApiError('print-receipt', err);

                      const errorCode = err?.error?.message;

                      this.notificationService.pushError(
                        'Fehler'.trans('notifications/error.Title'),
                        errorCode
                          ? errorCode.trans(`print-receipt/notifications/${errorCode}.Message`)
                          : 'Ein Fehler ist aufgetreten'.trans('notifications/error.Unknown')
                      );
                    }
                  });
              } else {
                this.isCheckoutProcessing.set(false);
                printKitchenSlip();
              }
            };

            if (isOnlineOrderCheckout) {
              this.isCheckoutProcessing.set(false);
            } else if (this._cartCheckout.item.transactionId) {
              if (selectedItemsHavePrintableTypes) {
                this._workerService.printTransaction(this._cartCheckout.item.transactionId, false)
                  .pipe(
                    finalize(() => fn())
                  )
                  .subscribe({
                    error: (err: ApiErrorResponse) => {
                      this._loggingService.captureApiError('print-transaction', err);

                      const errorCode = err?.error?.message;

                      this.notificationService.pushError(
                        'Fehler'.trans('notifications/error.Title'),
                        errorCode
                          ? errorCode.trans(`print-transaction/notifications/${errorCode}.Message`)
                          : 'Ein Fehler ist aufgetreten'.trans('notifications/error.Unknown')
                      );
                    }
                  });
              } else {
                fn();
              }
            } else {
              fn();
            }

            this.reservationsStore.rebookReservation.set(null);

            if (this._autoCloseCart) {
              if (res.data.item.basket.status !== CartStatuses.PartiallyPaid) {
                this.closeCart();
              } else {
                this.continue();
              }
            }
          },
          error: (err: ApiErrorResponse) => {
            this.isCheckoutProcessing.set(false);

            const errorCode = err?.error?.message;
            const cinevilleMessage = CinevilleUtils.resolveCinevilleErrorMessage(err?.error);
            if (errorCode !== ApiErrorCodes.SalesTaxNotFound) {
              this._loggingService.captureApiError('checkout', err, this.buildCartSnapshot());
            }

            const errorMessage =
              cinevilleMessage ??
              (errorCode === 'AcardoRedemptionRejected'
                ? 'Der Acardo-Gutschein wurde vom Partner abgelehnt.'.trans(
                    'checkout-cart/notifications/AcardoRedemptionRejected.Message'
                  )
                : errorCode === ApiErrorCodes.SalesTaxNotFound
                  ? 'Für diese Position ist keine gültige Mehrwertsteuer hinterlegt.'.trans(
                      'checkout-cart/notifications/SalesTaxNotFound.Message'
                    )
                  : errorCode
                    ? errorCode.trans(`checkout-cart/notifications/${errorCode}.Message`)
                    : 'Ein Fehler ist aufgetreten'.trans('notifications/error.Unknown'));

            this.notificationService.pushError('Fehler'.trans('notifications/error.Title'), errorMessage);
          }
        });
    };

    if (this.paySeparatelyStore.refundAmount() > 0) {
      this.isCheckoutProcessing.set(true);

      if (this.refundCartPaymentMethod === CartPaymentMethods.CustomerCard) {
        this.addPayment(
          CartPaymentMethods.CustomerCardOut,
          this.paySeparatelyStore.refundAmount(),
          { refundCardCode: this.refundCardCode },
          checkoutCart
        );
      } else if (this.refundCartPaymentMethod === CartPaymentMethods.Cash) {
        this.addPayment(CartPaymentMethods.CashOut, this.paySeparatelyStore.refundAmount(), {}, checkoutCart);
      } else if (this.refundCartPaymentMethod === CartPaymentMethods.VoucherOut) {
        this.addPayment(CartPaymentMethods.VoucherOut, this.paySeparatelyStore.refundAmount(), {}, checkoutCart);
      } else if (this.refundCartPaymentMethod === CartPaymentMethods.Invoice) {
        this.addPayment(
          CartPaymentMethods.InvoiceOut,
          this.paySeparatelyStore.refundAmount(),
          { customerId: this.selectedCustomerForInvoice.id },
          checkoutCart
        );
      } else if (this.refundCartPaymentMethod === CartPaymentMethods.Card) {
        if (this.isEcStandalone) {
          this.isCheckoutProcessing.set(false);
          this.openStandaloneEcConfirmationModal(
            this.paySeparatelyStore.refundAmount(),
            () => this.addPayment(
              CartPaymentMethods.CardOut,
              this.paySeparatelyStore.refundAmount(),
              {},
              checkoutCart
            )
          );
          return;
        }

        this.ecTerminalActionLogsModalComponent.open();

        this._cartService.startCardRefund()
          .subscribe({
            next: () => {
              this.addPayment(
                CartPaymentMethods.CardOut,
                this.paySeparatelyStore.refundAmount(),
                {},
                () => {
                  AlertModalComponent.openAlertModal(
                    'Card Refund'.trans('cart/summary-modal/card-refund-success-alert-modal.Title'),
                    'Card refund success.'.trans('cart/summary-modal/card-refund-success-alert-modal.Title'),
                    this.destroyRef,
                    () => {
                      this.ecTerminalActionLogsModalComponent.close();
                      checkoutCart();
                    }
                  );
                }
              );
            },
            error: ({ error }: ApiErrorResponse) => {
              this.ecTerminalActionLogsModalComponent.close();
              this.isCheckoutProcessing.set(false);
              this.notificationService.pushError(
                'Fehler'.trans('notifications/error.Title'),
                this.cardErrorText(error.message, 'start-card-refund')
              );
            }
          });
      }
    } else {
      checkoutCart();
    }
  }

  public confirmDescription(val: { description: string }): void {
    if (this._currentModalType === 'cancel') {
      this.description = val.description;
      this._currentModalType = null;

      if (this.shouldShowFreeEntryRemarkModal()) {
        this._currentModalType = 'freeEntry';
        this.transactionRemark = null;
        setTimeout(() => this.descriptionModalComponent.open({ modalType: 'freeEntry' }));
        return;
      }
    } else if (this._currentModalType === 'freeEntry') {
      this.transactionRemark = val.description;
      this._currentModalType = null;
    }

    if (this.isRefund) {
      this.paymentMethodPickerModalComponent.open();
    } else if (this.areReservations && !this.hasAnyCardWithCustomerId && !this.reservationsStore.rebookReservation()) {
      this.openReservationCustomerNameModal();
    } else {

      this.checkoutCart();
    }
  }

  public confirmRefundCartPaymentMethod(): void {
    this.areReservations && !this.hasAnyCardWithCustomerId
      ? this.openReservationCustomerNameModal()
      : this.checkoutCart();
  }

  public setOrderAmount(): void {
    this._isOrderAmountSet = true;

    if (this.cartStore.cart().totalPrice > 0) {
      this.updateAmountValue((this.cartStore.cart().totalPrice * 100).toString());
    } else {
      this.handleKeyboardClear();
    }
  }

  public handleKeyboardClear(): void {
    this.updateAmountValue('');
  }

  public handleKeyboardDeleteBack(): void {
    this.updateAmountValue(this.amountValue.substring(0, this.amountValue.length - 1));
  }

  public handleKeyboardKeyClick(val: string): void {
    if (this._isOrderAmountSet) {
      this._isOrderAmountSet = false;
      this.handleKeyboardClear();
    }

    this.updateAmountValue(this.amountValue ? `${this.amountValue}${val}` : val);
  }

  public generateInvoice(noInvoicePayment = false): void {
    this.isCheckoutProcessing.set(true);

    const generateInvoice = (): void => {
      this._transactionsService.generateInvoice(this.transactionId)
        .pipe(
          finalize(() => this.isCheckoutProcessing.set(false))
        )
        .subscribe({
          next: res =>
            this.pdfViewerModalComponent.open({ base64Content: res.data.base64Content }),
          error: ({ error }: ApiErrorResponse) => {
            this.notificationService.pushError(
              'Fehler'.trans('notifications/error.Title'),
              error.message.trans(`generate-invoice/notifications/${error.message}.Message`)
            );
          }
        });
    };

    if (noInvoicePayment) {
      this._transactionsService.assignCustomerToTransaction(
        this.selectedCustomerForInvoice.isAnonym ? null : this.selectedCustomerForInvoice.id,
        this.transactionId
      )
        .subscribe({
          next: generateInvoice,
          error: ({ error }: ApiErrorResponse) => {
            this.isCheckoutProcessing.set(false);

            this.notificationService.pushError(
              'Fehler'.trans('notifications/error.Title'),
              error.message.trans(`assign-customer-to-transaction/notifications/${error.message}.Message`)
            );
          }
        });
    } else {
      generateInvoice();
    }
  }

  public addPayment(
    method: CartPaymentMethods,
    amount = this.contentEditableInputComponent.value.fromCurrency(),
    additionalDetails = {},
    successCb?: () => void,
  ): void {
    if (!this.terminalsStore.isPaymentMethodAllowed(method)) {
      this.resetCheckoutProcessingState();

      return;
    }

    this.isPaymentAdding.set(true);

    this._cartService.addPaymentToCart(
      {
        method,
        amount,
        ...additionalDetails,
      }
    )
      .pipe(
        finalize(() => this.isPaymentAdding.set(false))
      )
      .subscribe({
        next: () => {
          if (successCb) {
            successCb();
          }

          if (
            this.cartStore.cart().payments
              .filter(
                p => p.method === CartPaymentMethods.Cash || p.method === CartPaymentMethods.CashOut
              )
              .length === 1 &&
            this.terminalParametersMap()[this.terminalParameterKeys.CSH_SAFE_PORT]
          ) {
            this.openSafe();
          }
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`add-payment-to-cart/notifications/${error.message}.Message`)
          );
        }
      });
  }

  /**
   * Mirrors legacy CXCash CDlgCXCash::PrintEC: only when the EC terminal is configured to
   * print on the POS/ECR (receipt lines are buffered on the worker) ask whether to print the
   * customer copy, then forward the buffered slip. The merchant copy is always printed by the
   * worker. When the terminal prints on its own device, nothing is asked or sent.
   */
  private printBufferedEcReceiptWithPrompt(continueCb: () => void): void {
    if (!this.isEcPrintLocationPos || this.isEcSumUp) {
      continueCb();
      return;
    }

    this._ecReceiptPrintService.promptAndPrint(this.destroyRef, { afterAnswered: continueCb });
  }

  public startQuickInvoicePayment(): void {
    if (!this.terminalsStore.isPaymentMethodAllowed(CartPaymentMethods.Invoice)) {
      return;
    }

    this._quickCloseAfterInvoice = true;
    this.selectedCustomerForInvoice = null;
    this.refundCartPaymentMethod = CartPaymentMethods.Cash;
    this.customersSearchModalComponent.open();
  }

  public cancelQuickInvoice(): void {
    this._quickCloseAfterInvoice = false;
  }

  private cardErrorText(message: string, flow: 'start-card-payment' | 'start-card-refund'): string {
    const defaults: Record<string, string> = {
      SumUpNotConfigured: 'SumUp ist an dieser Kasse nicht konfiguriert.',
      SumUpReaderOffline: 'SumUp-Terminal ist offline.',
      SumUpReaderBusy: 'SumUp-Terminal ist belegt.',
      SumUpCheckoutInProgress: 'Kartenzahlung läuft bereits.',
      SumUpPaymentFailed: 'Kartenzahlung fehlgeschlagen.',
      SumUpPaymentCancelled: 'Kartenzahlung abgebrochen.',
      SumUpStatusUnknown: 'Kartenzahlung: Status unbekannt. Bitte nicht erneut belasten, bevor der Status geprüft ist.',
      SumUpUnbookedPayment: 'Es gibt eine nicht verbuchte SumUp-Zahlung mit anderem Betrag. Bitte Status in SumUp prüfen.',
      SumUpNoOriginalPayment: 'Keine SumUp-Zahlung zur Erstattung gefunden.',
      SumUpMultipleOriginalSales: 'Erstattung betrifft mehrere Verkäufe. Bitte einzeln erstatten.',
      SumUpRefundExceedsPayment: 'Erstattungsbetrag ist höher als die SumUp-Zahlung.',
      SumUpRefundNotPossible: 'SumUp kann diese Zahlung derzeit nicht erstatten. Bitte in einigen Minuten erneut versuchen.'
    };
    const text = defaults[message] ?? message;
    return text.trans(`${flow}/notifications/${message}.Message`);
  }

  private recordConfirmedCardPaymentAndCheckout(amount: number, autoClose = false): void {
    this.addPayment(
      CartPaymentMethods.Card,
      amount,
      {},
      () => {
        this.ecTerminalActionLogsModalComponent.close();
        if (this.cartStore.cart().paidAmount >= this.paySeparatelyStore.toPayAmount()) {
          this.onCheckoutButtonClick(autoClose);
        }
      }
    );
  }

  private openStandaloneEcConfirmationModal(amount: number, onConfirm: () => void): void {
    ConfirmationModalComponent.openConfirmationModal(
      'Manual EC payment'.trans('cart/summary-modal/manual-ec-payment-confirmation-modal.Title'),
      'Amount: [[amount]] [[currency]]. Was the EC payment completed successfully?'
        .trans('cart/summary-modal/manual-ec-payment-confirmation-modal.Text', [
          ['amount', amount.toCurrency()],
          ['currency', this.languageConfig.currency.short]
        ]),
      this.destroyRef,
      onConfirm
    );
  }

  public printLastReceipt(): void {
    const terminalSessionId = this.terminalsStore.selectedTerminal()?.currentSession?.id;
    if (!terminalSessionId) {
      this.notificationService.pushError(
        'Fehler'.trans('notifications/error.Title'),
        'No active terminal session found'.trans('notifications/no-active-terminal-session-found.Message')
      );
      return;
    }

    this.isPrintLastReceiptLoading.set(true);

    this._workerService.printLastReceipt(terminalSessionId)
      .pipe(
        finalize(() => this.isPrintLastReceiptLoading.set(false))
      )
      .subscribe({
        next: () => {
          this.notificationService.pushSuccess(
            'Erfolg'.trans('notifications/success.Title'),
            'Receipt printed successfully.'.trans('cart/summary-modal/notifications/print-last-receipt-success.Message')
          );
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`print-last-receipt/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public hasSelectedTicketsInGroup(group: { tickets: { itemId: number; position: number }[] }): boolean {
    return group.tickets.some(t => !this.paySeparatelyStore.isHidden(t.itemId, t.position));
  }

  private get transactionId(): number {
    return this._cartCheckout?.item.transactionId ?? this.cartStore.cart().transactionId;
  }

  private updateAmountValue(val: string): void {
    this.amountValue = val;

    if (this.contentEditableInputComponent) {
      this.contentEditableInputComponent.value = this.amountValue;
    }
  }

  private get isRefund(): boolean {
    const amountToCheck = this.paySeparatelyStore.isPaySeparatelyModeEnabled()
      ? this.paySeparatelyStore.selectedItemsTotal() < 0 
        ? Math.abs(this.paySeparatelyStore.selectedItemsTotal()) 
        : 0
      : this.cartStore.cart().remainingAmount;
    return amountToCheck > 0 &&
      !this.cartStore.cart().payments.some(p => p.method === CartPaymentMethods.Cash);
  }

  private openReservationCustomerNameModal(): void {
    this._cartService.getReservationCode()
      .subscribe({
        next: res => {
          this.reservationCustomerNameModalComponent.open(res.data.reservationCode);
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`summary-modal/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private shouldShowFreeEntryRemarkModal(): boolean {
    const freeEntryRemarkParam = +this.terminalParametersMap()[TerminalParameterKeys.CSH_SHOW_REMARK_DLG_FREEENTRIES] as number;

    if (!freeEntryRemarkParam || freeEntryRemarkParam === 0) {
      return false;
    }

    const hasFreeEntry = this.hasFreeEntry();
    const hasCardFreeEntry = this.hasCardFreeEntry();

    if (freeEntryRemarkParam === 1) {
      return hasFreeEntry;
    }

    if (freeEntryRemarkParam === 2) {
      return hasCardFreeEntry;
    }

    if (freeEntryRemarkParam === 3) {
      return hasFreeEntry || hasCardFreeEntry;
    }

    return false;
  }

  private hasFreeEntry(): boolean {
    return this.cartStore.cart().items.some(item =>
      item.itemType === CartItemTypes.Ticket &&
      (item.price === 0 || item.totalPrice === 0) &&
      !item.cardNumber
    );
  }

  private navigateToNextUpcomingShowIfEnabled(): void {
    if (this.terminalParametersMap()[TerminalParameterKeys.WEB_CASH_RETURN_TO_NEXT_SHOW] !== true) {
      return;
    }

    const nextShow = NextShowAfterSaleUtils.pickNextUpcomingShow(
      this.showsStore.shows(),
      this.showsStore.auditoriums()
    );

    if (!nextShow) {
      return;
    }

    const currentShowId = this.showsStore.selectedShow()?.id;

    if (currentShowId === nextShow.id) {
      return;
    }

    void this._router.navigateByUrl(`/shows/${nextShow.id}`.langUrl());
  }

  private resetModalState(): void {
    this._cartCheckout = null;
    this.isEcChargeConfirmed.set(false);
    this.selectedCustomerForInvoice = null;
    this.refundCartPaymentMethod = CartPaymentMethods.Cash;
    this.refundCardCode = null;
    this.description = null;
    this.transactionRemark = null;
    this.customerName = null;
    this.reservationCode = null;
    this.amountValue = null;
    this._currentModalType = null;
  }

  private hasCardFreeEntry(): boolean {
    return this.cartStore.cart().items.some(item =>
      item.itemType === CartItemTypes.Ticket &&
      (item.price === 0 || item.totalPrice === 0) &&
      !!item.cardNumber
    );
  }

  private showOnlineOrderDocuments(transactionId: number): void {
    if (!transactionId) {
      return;
    }

    this.openOnlineOrderReport(ReportKeys.CustomerCreditCardBill, transactionId, () =>
      this.openOnlineOrderReport(ReportKeys.CustomerDelivery, transactionId)
    );
  }

  private openOnlineOrderReport(reportKey: ReportKeys, transactionId: number, afterClose?: () => void): void {
    this._reportsService.generateReport(reportKey, transactionId)
      .subscribe({
        next: res => {
          if (afterClose) {
            this.pdfViewerModalComponent.onClose
              .pipe(take(1), takeUntilDestroyed(this.destroyRef))
              .subscribe(afterClose);
          }

          this.pdfViewerModalComponent.open({ base64Content: res.data.base64Content });
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`online-orders/documents/notifications/${error.message}.Message`)
          );
          afterClose?.();
        }
      });
  }
}
